import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CurrencyDollar, Gauge, ToggleRight, Wrench, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const FLAG_LABELS = {
  ai_extraction: "AI email extraction",
  email_import: "Email forwarding / import",
  exports: "PDF / Excel exports",
  notifications: "Email notifications",
  analytics: "Charts & analytics",
  cloud_backup: "Cloud backup",
  projections: "Future pay projections",
  beta_features: "Beta features",
};

export default function AdminPlatform() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data } = useQuery({ queryKey: ["admin-config"], queryFn: async () => (await api.get("/admin/config")).data });
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/config", {
      pro_price_monthly: parseFloat(form.pro_price_monthly) || 0,
      pro_price_annual: parseFloat(form.pro_price_annual) || 0,
      free_paid_duty_limit: parseInt(form.free_paid_duty_limit) || 0,
      free_history_days: parseInt(form.free_history_days) || 0,
      trial_days: parseInt(form.trial_days) || 0,
      feature_flags: form.feature_flags,
      maintenance_mode: form.maintenance_mode,
      maintenance_message: form.maintenance_message,
    })).data,
    onSuccess: (d) => { setForm(d); qc.invalidateQueries({ queryKey: ["admin-config"] }); toast.success("Platform settings saved — live now."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!form) return <div className="text-muted-foreground" data-testid="admin-platform-loading">Loading…</div>;

  const setFlag = (k, v) => setForm({ ...form, feature_flags: { ...form.feature_flags, [k]: v } });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-platform-page">
      {/* Pricing */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4"><CurrencyDollar className="h-5 w-5 text-emerald-400" weight="bold" /><h2 className="font-bold">Pricing</h2></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Pro Monthly price</Label><Input type="number" step="0.01" data-testid="cfg-price-monthly" className="mt-1.5 bg-secondary/50" value={form.pro_price_monthly} onChange={(e) => setForm({ ...form, pro_price_monthly: e.target.value })} /></div>
          <div><Label>Pro Annual price</Label><Input type="number" step="0.01" data-testid="cfg-price-annual" className="mt-1.5 bg-secondary/50" value={form.pro_price_annual} onChange={(e) => setForm({ ...form, pro_price_annual: e.target.value })} /></div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">These control the prices displayed across the app. The amount actually charged is governed by your Stripe Price IDs — update those in Stripe (or Phase 3b) to change what customers are billed.</p>
      </Card>

      {/* Plan limits */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4"><Gauge className="h-5 w-5 text-sky-400" weight="bold" /><h2 className="font-bold">Free plan limits & trial</h2></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Free paid-duty entries</Label><Input type="number" data-testid="cfg-free-limit" className="mt-1.5 bg-secondary/50" value={form.free_paid_duty_limit} onChange={(e) => setForm({ ...form, free_paid_duty_limit: e.target.value })} /></div>
          <div><Label>Free history (days)</Label><Input type="number" data-testid="cfg-history-days" className="mt-1.5 bg-secondary/50" value={form.free_history_days} onChange={(e) => setForm({ ...form, free_history_days: e.target.value })} /></div>
          <div><Label>Trial length (days)</Label><Input type="number" data-testid="cfg-trial-days" className="mt-1.5 bg-secondary/50" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} /></div>
        </div>
      </Card>

      {/* Feature flags */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4"><ToggleRight className="h-5 w-5 text-primary" weight="bold" /><h2 className="font-bold">Feature flags</h2></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.keys(FLAG_LABELS).map((k) => (
            <div key={k} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5">
              <span className="text-sm">{FLAG_LABELS[k]}</span>
              <Switch checked={!!form.feature_flags?.[k]} onCheckedChange={(v) => setFlag(k, v)} data-testid={`cfg-flag-${k}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Turning a flag off disables that feature for all users (kill-switch), even Pro subscribers.</p>
      </Card>

      {/* Maintenance */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4"><Wrench className="h-5 w-5 text-amber-400" weight="bold" /><h2 className="font-bold">Maintenance mode</h2></div>
        <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5 mb-3">
          <span className="text-sm">Show a maintenance screen to non-admin users</span>
          <Switch checked={!!form.maintenance_mode} onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })} data-testid="cfg-maintenance" />
        </div>
        <Label>Maintenance message</Label>
        <Textarea data-testid="cfg-maintenance-message" className="mt-1.5 bg-secondary/50" rows={2} value={form.maintenance_message || ""} onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })} />
      </Card>

      <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="cfg-save" size="lg" className="rounded-md font-semibold">
        <FloppyDisk className="h-4 w-4 mr-2" weight="bold" /> {save.isPending ? "Saving…" : "Save platform settings"}
      </Button>
    </div>
  );
}
